import { useState, useEffect } from 'react'
import { Drawer, Button, Grid, Typography } from 'antd'
import { SkeletonSettings } from '../features/skeleton'
import { useSearchParams } from 'react-router-dom'
import {
  UserOutlined,
  MenuOutlined,
  SettingOutlined,
  WalletOutlined,
  CreditCardOutlined,
} from '@ant-design/icons'
import { useProfileContext } from '../context/profile'
import { AccountSettingsSection } from '../components/Settings/AccountSettingsSection'
import { PreferencesSection } from '../components/Settings/PreferencesSection'
import { CreditUsageSection } from '../components/Settings/CreditUsageSection'
import { BillingSection } from '../components/Settings/BillingSection'
import {
  SettingsMenu,
  PageContainer,
  LayoutContainer,
  SidebarContainer,
  SidebarHeader,
  ContentArea,
  MobileHeader,
} from './styles/SettingsPageStyles'

const { useBreakpoint } = Grid
const { Text } = Typography

type SettingsSection = 'account' | 'billing' | 'credits' | 'preferences'

export function SettingsPage() {
  const { loading } = useProfileContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionParam = searchParams.get('section') as SettingsSection | null
  const screens = useBreakpoint()
  const isMobile = !screens.md // Below 768px
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Initialize with section from URL or default to 'account'
  const [selectedSection, setSelectedSection] = useState<SettingsSection>(
    sectionParam && ['account', 'billing', 'credits', 'preferences'].includes(sectionParam)
      ? sectionParam
      : 'account'
  )

  // Update section when URL param changes
  useEffect(() => {
    if (sectionParam && ['account', 'billing', 'credits', 'preferences'].includes(sectionParam)) {
      setSelectedSection(sectionParam)
      // Clean up URL param after setting section
      if (searchParams.has('section')) {
        setSearchParams({}, { replace: true })
      }
    }
  }, [sectionParam, searchParams, setSearchParams])

  const menuItems = [
    {
      key: 'account',
      icon: <UserOutlined />,
      label: 'Account Settings',
    },
    {
      key: 'billing',
      icon: <CreditCardOutlined />,
      label: 'Billing',
    },
    {
      key: 'credits',
      icon: <WalletOutlined />,
      label: 'Credit Usage',
    },
    {
      key: 'preferences',
      icon: <SettingOutlined />,
      label: 'Preferences',
    },
  ]

  const handleMenuClick = (key: string) => {
    setSelectedSection(key as SettingsSection)
    if (isMobile) {
      setDrawerOpen(false)
    }
  }

  const renderSection = () => {
    switch (selectedSection) {
      case 'account':
        return <AccountSettingsSection />
      case 'billing':
        return <BillingSection />
      case 'credits':
        return <CreditUsageSection />
      case 'preferences':
        return <PreferencesSection />
      default:
        return <AccountSettingsSection />
    }
  }

  if (loading) {
    return <SkeletonSettings />
  }

  const menuContent = (
    <SettingsMenu
      mode="inline"
      selectedKeys={[selectedSection]}
      items={menuItems}
      onClick={({ key }) => handleMenuClick(key)}
    />
  )

  // Get current section label for mobile header
  const currentSectionLabel =
    menuItems.find((item) => item.key === selectedSection)?.label ?? 'Settings'

  // Desktop sidebar
  const renderDesktopSidebar = () => (
    <SidebarContainer>
      <SidebarHeader>
        <Text strong>Settings</Text>
      </SidebarHeader>
      {menuContent}
    </SidebarContainer>
  )

  return (
    <PageContainer $isMobile={isMobile}>
      {/* Mobile: menu toggle button */}
      {isMobile && (
        <MobileHeader>
          <Button icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)}>
            {currentSectionLabel}
          </Button>
        </MobileHeader>
      )}

      {/* Mobile drawer */}
      <Drawer
        title="Settings"
        placement="left"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ body: { padding: 0 }, wrapper: { width: 250 } }}
      >
        {menuContent}
      </Drawer>

      <LayoutContainer>
        {/* Desktop sidebar */}
        {!isMobile && renderDesktopSidebar()}

        <ContentArea $isMobile={isMobile} $hasDesktopSidebar={!isMobile}>
          {renderSection()}
        </ContentArea>
      </LayoutContainer>
    </PageContainer>
  )
}
